import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import Loader from 'components/Loader';
import ExpenseModal from 'components/modals/expenses/ExpenseModal';
import { useModal } from 'components/modals/useModal';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Button } from 'components/ui/button';
import { VIEW_PRESERVED_PARAM_KEYS } from 'utils/viewParam';
import { mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import MoneyDisplay from 'components/util/MoneyDisplay';
import useAuth from 'hooks/useAuth.ts';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useAddExpenseMutation, useLazyGetExpensesGroupedQuery } from 'store/api/expenses';
import { useStartExpenseExportMutation } from 'store/api/exports';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { EmployeeListItemMinDto } from 'types/employees';
import {
    ExpenseCreateDto,
    ExpenseForm,
    ExpenseGroupedQueryResponse,
    ExpenseListItem,
    ExpenseTypeGroup,
} from 'types/expenses';
import { EXPORT_CONFIG } from 'types/exports';
import { errorAlert } from 'utils/alert';
import { PaginatedFieldPassiveFilter } from 'utils/filterUtil.ts';
import { getOptionsFromClassifier, getValidOptionsFromClassifier } from 'utils/formUtil';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { cn } from 'utils/cn';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import ExpensesGroupedTier2Table from './ExpensesGroupedTier2Table';
import {
    buildExpensesFilterConfig,
    EXPENSES_FILTER_STATUS_TYPES,
    ExpensesFilterGroup,
    ExpensesFilterId,
} from './expensesFilterConfig';
import { groupedTypeColumnWeight } from './expensesGroupedColumns';
import { sortByNumericAccessor, sortTier1ByTranslatedType } from './expensesGroupedSort';

const USED_CLASSIFIERS = ['EXPENSE_TYPE', 'EXPENSE_STATUS_TYPE', 'CURRENCY', 'INVOICE_STATUS_TYPE'];

type Props = {
    extraHeaderComponent?: React.ReactNode;
    quickFilterTrailing?: React.ReactNode;
    passiveFilter?: string;
    disableExport?: boolean;
    disabledGroups?: ExpensesFilterGroup[];
    disabledFilterIds?: ExpensesFilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<ExpensesFilterId>[];
    canRememberFilters?: boolean;
};

const EMPTY_FILTER = '';

/**
 * Tier 1 of the grouped Expenses view: per-expense-type rows with the list table's
 * sticky totals row beneath the header (driven by per-column `renderTotal`). Tier 1
 * expansion drops in the Tier 2 employee table via `renderExpandedContent`, which
 * itself nests the Tier 3 individual-lines table per employee. Column widths are kept
 * consistent across tiers via shared constants and `table-layout: fixed` so the
 * per-currency columns line up vertically.
 */
/** A grouped-expenses column. The totals row is the per-currency amount map the endpoint returns
 *  under `totalsByCurrency`, so `meta.renderTotal` takes that shape. */
type GroupedExpenseColumn = DataTableColumnDef<ExpenseTypeGroup, Record<string, number> | undefined>;

const ExpensesGroupedTable = ({
    extraHeaderComponent,
    quickFilterTrailing,
    passiveFilter = EMPTY_FILTER,
    disableExport = false,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    canRememberFilters = true,
}: Props): JSX.Element => {
    const { user } = useAuth();
    const userId = user?.id;
    const { openModal, closeModal } = useModal();
    const [addExpense] = useAddExpenseMutation();
    const hasPermissionToExportExpenses = useHasPermissions(['EXPORT.EXPENSES']);

    const [fetchData, { data, isFetching }] = useLazyGetExpensesGroupedQuery();
    const { data: tenant, isLoading: isLoadingTenant } = useGetCurrentTenantQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });
    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(
        () => currentEmployeeQueryData?.content.at(0),
        [currentEmployeeQueryData]
    );

    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartExpenseExportMutation, {
        entityType: 'EXPENSE',
        entityName: t('expenses.export.entity-name'),
        fileName: EXPORT_CONFIG.EXPENSE.fileName,
    });

    // Tenant currencies, sorted A→Z by code. The grouped endpoint
    // aggregates per `originalCurrencyTypeCode` straight off the expense rows, so its
    // `totalsByCurrency` keys can include currencies not present in the tenant CURRENCY
    // classifier. Drive the columns from the classifier (tenant-scoped) so those stray keys
    // don't surface as columns. Within the classifier, hide currencies whose
    // validity window has lapsed unless the current filter window still surfaces expenses
    // priced in them — that way expired-and-unused codes drop off, but historical data in an
    // expired currency stays visible.
    const currencyCodes = useMemo<string[]>(() => {
        const tenantCodes = new Set((classifiers?.['CURRENCY']?.values ?? []).map((v: { code: string }) => v.code));
        const validCodes = new Set(
            getValidOptionsFromClassifier<string, string>(classifiers?.['CURRENCY']).map(({ value }) => value)
        );
        const presentInData = Object.keys(data?.totalsByCurrency ?? {});
        const codes = [...tenantCodes].filter((code) => validCodes.has(code) || presentInData.includes(code));
        return codes.toSorted((a, b) => a.localeCompare(b));
    }, [classifiers, data]);

    // The list table assembles the active RSQL filter from the user's main/quick/passive
    // filters and reports it via onFilterChange. We capture it here so Tier 2 can compose it
    // with `expenseTypeCode==X;employee.id==Y` when lazy-fetching Tier 3 lines.
    const [activeFilter, setActiveFilter] = useState<string>('');

    // The /v1/expenses/grouped endpoint ignores the `sort` arg and returns the entire tree
    // unordered, so Tier 1 sort happens client-side. Track the active sort string here and apply
    // it in `dataWithTotals`. Empty string → fall back to A→Z by translated expense-type name.
    const [tier1Sort, setTier1Sort] = useState<string>('');

    // Per-(typeCode, employeeId) Tier 2 expansion state lives at the Tier 1 parent so it
    // survives Tier 1 collapse-and-reopen (which unmounts the Tier 2 component otherwise).
    const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
    const employeeKey = useCallback((typeCode: string, employeeId: number) => `${typeCode}::${employeeId}`, []);
    const isEmployeeExpanded = useCallback(
        (typeCode: string, employeeId: number) => expandedEmployees.has(employeeKey(typeCode, employeeId)),
        [expandedEmployees, employeeKey]
    );
    const toggleEmployeeExpanded = useCallback(
        (typeCode: string, employeeId: number) => {
            setExpandedEmployees((prev) => {
                const key = employeeKey(typeCode, employeeId);
                const next = new Set(prev);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
        },
        [employeeKey]
    );

    const handleExpenseCreate = useCallback(
        async (expense: ExpenseCreateDto) => {
            try {
                await addExpense({ ...expense }).unwrap();
                return 0;
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
                return 1;
            }
        },
        [addExpense]
    );

    const handleDuplicateExpense = useCallback(
        (expenseToDuplicate: ExpenseListItem): void => {
            const preSelectedEmployee = currentEmployeeData;
            const initialData: ExpenseForm = {
                id: undefined,
                externalId: null,
                expenseType:
                    (
                        getOptionsFromClassifier(classifiers['EXPENSE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expenseToDuplicate.expenseTypeCode) ?? null,
                currencyType:
                    (
                        getOptionsFromClassifier(classifiers['CURRENCY']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === expenseToDuplicate.originalCurrencyTypeCode) ?? null,
                description: expenseToDuplicate.description ?? '',
                expenseAmount: expenseToDuplicate.expenseAmountOriginalCurrency?.toString() ?? '',
                expenseDate: expenseToDuplicate.expenseDate
                    ? formatDateStringInput(new Date(expenseToDuplicate.expenseDate))
                    : formatDateStringInput(new Date()),
                isNonBillable: expenseToDuplicate.isNonBillable ?? false,
                employee: expenseToDuplicate.employee
                    ? expenseToDuplicate.employee
                    : preSelectedEmployee
                      ? { id: preSelectedEmployee.id, name: preSelectedEmployee.name }
                      : null,
                matter: null,
                effortId: null,
                timecardId: null,
            };
            openModal(
                <ExpenseModal
                    onChange={(value) => {
                        void handleExpenseCreate(value);
                    }}
                    initialData={initialData}
                    onClose={closeModal}
                    titleId="expenses.duplicate-expense"
                    readOnly={false}
                    preselectMatterId={expenseToDuplicate.matter?.id}
                    hideSelectMatter={false}
                    tenant={tenant}
                />,
                closeModal
            );
        },
        [classifiers, currentEmployeeData, closeModal, openModal, handleExpenseCreate, tenant]
    );

    // `totals` is mirrored from `totalsByCurrency` so the list table picks it up and renders
    // the sticky totals row via per-column `renderTotal`. Tier 1 sort happens client-side: a
    // numeric column sort routes through `sortTier1ByCurrencyAmount`, otherwise we fall back to
    // the locale-aware translated-type sort (see expensesGroupedSort.ts).
    const dataWithTotals = useMemo<
        (ExpenseGroupedQueryResponse & { totals: Record<string, number> }) | undefined
    >(() => {
        if (!data) return undefined;
        const [sortId, sortDir] = tier1Sort.split(',');
        const isCurrencySort = sortId?.startsWith('amount-');
        const sortedContent = isCurrencySort
            ? sortByNumericAccessor(
                  data.content,
                  (g) => g.amountsByCurrency?.[sortId.slice('amount-'.length)],
                  sortDir === 'desc'
              )
            : sortTier1ByTranslatedType(data.content, classifiers?.['EXPENSE_TYPE'], sortDir === 'desc');
        return {
            ...data,
            content: sortedContent,
            totals: data.totalsByCurrency ?? {},
        };
    }, [data, classifiers, tier1Sort]);

    const columns = useMemo<GroupedExpenseColumn[]>(() => {
        const typeColumn: GroupedExpenseColumn = {
            header: () => <div className="font-semibold">{t('expenses.type')}</div>,
            accessorKey: 'expenseTypeCode',
            enableSorting: false,
            meta: {
                widthWeight: groupedTypeColumnWeight(currencyCodes.length),
                minPx: 108,
            },
            cell: ({ row }) => (
                <p className="text-body1 font-semibold">
                    {getClassifierText(classifiers?.['EXPENSE_TYPE'], row.original.expenseTypeCode)}
                </p>
            ),
        };

        const currencyColumns: GroupedExpenseColumn[] = currencyCodes.map((code) => ({
            header: () => (
                <div className="font-semibold">
                    <FormattedMessage id="expenses.grouped.amount-in" values={{ currency: code }} />
                </div>
            ),
            id: `amount-${code}`,
            accessorFn: (row: ExpenseTypeGroup) => row.amountsByCurrency?.[code],
            meta: {
                // Every currency column carries the same weight, so they share whatever the type
                // column leaves. The floor deliberately sits BELOW the
                // "Amount in XXX" header's own width (~142px), so the effective floor is the one the
                // managed layout measures from each column's real header / totals content — a higher
                // static floor only widens the table past that content for no gain.
                widthWeight: 1,
                minPx: 130,
                // Threaded through to the header, totals and body cells by the DataTable engine so the
                // label and the amounts all sit flush-right.
                textAlign: 'right',
                renderTotal: (totals: Record<string, number> | undefined) => {
                    const value = totals?.[code];
                    return value != null ? <MoneyDisplay amount={value} currency={code} /> : <>-</>;
                },
            },
            // Both cells render the amount as INLINE content (MoneyDisplay already nowraps itself).
            // A block-level wrapper here would fill the cell, and the managed layout measures the
            // totals cell with a Range over its contents — a full-width block would report the
            // column's current width as its content floor, ratcheting the floor on every resolve so
            // the column could never shrink again.
            cell: ({ row }) => {
                const value = row.original.amountsByCurrency?.[code];
                return value != null ? <MoneyDisplay amount={value} currency={code} /> : <>-</>;
            },
        }));

        const expandColumn: GroupedExpenseColumn = {
            header: '',
            id: 'expand',
            enableSorting: false,
            meta: {
                fixedPx: 64,
                // Centered so the chevron sits as its own column instead of clinging to the right
                // edge of the table next to the rightmost currency cell, mirroring how
                // `currencyColumns` above right-aligns its amount cells.
                textAlign: 'center',
            },
            cell: ({ row }) => {
                const label =
                    getClassifierText(classifiers?.['EXPENSE_TYPE'], row.original.expenseTypeCode) ??
                    row.original.expenseTypeCode;
                return (
                    <Button
                        variant="ghost"
                        // 30px and middle-aligned, like the nested tiers' chevron; grey at rest and on hover.
                        className={cn(
                            'text-grey-900/54 hover:text-grey-900/54 size-7.5 p-0 align-middle',
                            GLYPH_OWN_SIZE
                        )}
                        onClick={row.getToggleExpandedHandler()}
                        aria-label={t(
                            row.getIsExpanded()
                                ? 'expenses.grouped.collapse-type-row'
                                : 'expenses.grouped.expand-type-row',
                            { label }
                        )}
                    >
                        <ExpandMoreRoundedIcon
                            className={cn(
                                'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                row.getIsExpanded() && '-scale-y-100'
                            )}
                        />
                    </Button>
                );
            },
        };

        return [typeColumn, ...currencyColumns, expandColumn];
    }, [classifiers, currencyCodes]);

    const renderExpandedContent = useCallback(
        (row: ExpenseTypeGroup) => (
            <ExpensesGroupedTier2Table
                typeGroup={row}
                currencyCodes={currencyCodes}
                expenseStatusClassifier={classifiers?.['EXPENSE_STATUS_TYPE']}
                onDuplicate={handleDuplicateExpense}
                isEmployeeExpanded={(employeeId: number) => isEmployeeExpanded(row.expenseTypeCode, employeeId)}
                onToggleEmployee={(employeeId: number) => toggleEmployeeExpanded(row.expenseTypeCode, employeeId)}
                activeFilter={activeFilter}
            />
        ),
        [currencyCodes, classifiers, handleDuplicateExpense, isEmployeeExpanded, toggleEmployeeExpanded, activeFilter]
    );

    const expenseStatusTypeOptions = useMemo(
        () => mapToFilterOptions(EXPENSES_FILTER_STATUS_TYPES, classifiers['EXPENSE_STATUS_TYPE']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () =>
            buildExpensesFilterConfig({
                classifiers,
                expenseStatusTypeOptions,
                passiveFilter,
                disabledGroups,
                disabledFilterIds,
                paginatedFieldPassiveFilters,
            }),
        [
            classifiers,
            passiveFilter,
            expenseStatusTypeOptions,
            disabledFilterIds,
            disabledGroups,
            paginatedFieldPassiveFilters,
        ]
    );

    if (isLoadingClassifiers || isLoadingCurrentEmployeeQueryData || isLoadingTenant) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ExpenseTypeGroup, ExpenseGroupedQueryResponse>
            columnConfigId="expenses-grouped"
            preservedParamKeys={VIEW_PRESERVED_PARAM_KEYS}
            columns={columns}
            data={dataWithTotals}
            isLoading={isFetching}
            pluralTextId="expenses.expenses"
            singularTextId="expenses.expense"
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={Number.MAX_SAFE_INTEGER}
            renderExpandedContent={renderExpandedContent}
            extraHeaderComponent={extraHeaderComponent}
            quickFilterTrailing={quickFilterTrailing}
            onFilterChange={setActiveFilter}
            onSortChange={setTier1Sort}
            exportHookResults={
                disableExport || !hasPermissionToExportExpenses
                    ? undefined
                    : [
                          {
                              labelKey: 'export',
                              isBusy: isExportDataFetching,
                              triggerManualDownload,
                          },
                      ]
            }
            canRememberFilters={canRememberFilters}
        />
    );
};

export default ExpensesGroupedTable;
